import { differenceInDays, format } from 'date-fns';
import { Link } from 'react-router';
import { Box, Center, Column, Row, SmartTooltip, Text } from '~/commonComponents';
import { isPartialShift } from '~/commonComponents/CommonFunctions';
import { UserSubProfession } from '~/enums/UserProfession';
import { useAppSelector, useUserSelector } from '~/hooks';
import InfoIcon from '~/images/icons/Info';
import { IMultipleCounters } from '~/models';
import { Colors } from '~/themes/colors';
import { getProfessionalPayChargeDate } from '~/utils/chargeTimeline';
import { EducationUtils } from '~/utils/EducationUtils';
import { PartialShift } from '../../CandidateList/components/PartialShift';
import { renderOfferExpirationDateTime } from '../../counterJobs/RenderFunc';
import { isEmployeeOptionShift } from '../../WorkerClassification/utils';
import { HighlightedLines } from './HighlightedLines';
import { useMemo } from 'react';

const ConvenienceFeeTooltip =
  'GoTu charges a 3.5% convenience fee on Credit Card transactions. If you do not wish to be charged the convenience fee, please update your billing to E-check/ACH.';

const OvertimeTooltip =
  'Overtime pay (1.5x rate) is required for hours that exceed the state’s daily and/or weekly regular working hour limits.';

interface IShiftDetailsProps {
  currentCounter: IMultipleCounters;
  hasBorderStyle?: boolean;
  isLiteVersion?: boolean;
}

export const ShiftDetails = ({
  currentCounter,
  hasBorderStyle = true,
  isLiteVersion = false,
}: IShiftDetailsProps) => {
  const currentUser = useUserSelector();
  const { job } = useAppSelector((state) => state.job);

  const {
    isOnsiteInterview,
    job_datetime: jobDateTime,
    local_date: localDate,
    local_end_time: localEndTime,
    local_start_time: localStartTime,
    offer_owner: offerOwner,
    rate,
    specialty,
  } = job || {};
  const { payment } = currentCounter || {};

  const chargeDate = jobDateTime ? format(getProfessionalPayChargeDate(jobDateTime), 'MM/dd/yyyy') : '';
  const startDiff = jobDateTime ? differenceInDays(new Date(jobDateTime), new Date()) : 0;

  const totalAmount = payment?.totalAmount;
  const tempMeeFee = payment?.tempmeeFee;
  const tempMeeFeePercent = payment?.tempmeeFeeRatio
    ? (payment?.tempmeeFeeRatio * 100).toFixed(1)
    : 0;
  const convenienceFee = currentCounter?.payment?.convenienceFee ?? 0;
  const convenienceFeePercent = currentCounter?.payment?.convenienceFeeRatio
    ? (currentCounter?.payment?.convenienceFeeRatio * 100).toFixed(1)
    : 0;

  const hasConvenienceFee = convenienceFeePercent !== 0 && convenienceFee;
  const hasOvertimeHours = job?.regularAndOvertimeHours?.overtime > 0;
  const hasRegularHours = job?.regularAndOvertimeHours?.regular > 0;
  const hasTotalOvertime = job?.totalOvertime > 0;
  const hasTotalRegular = job?.totalRegular > 0;
  const isCounterHours = currentCounter ? isPartialShift(currentCounter) : null;
  const isCounterRate = currentCounter?.candidateRate !== rate;

  const fmtProfessionName = useMemo(() => {
    if (job?.subProfession === UserSubProfession.EFDA) {
      return 'Expanded Functions DA';
    }

    return job?.professionName;
  }, [job?.professionName, job?.subProfession]);

  return (
    <Column
      borderWidth={hasBorderStyle ? 1 : 0}
      borderColor={Colors.neutral_150}
      borderStyle="solid"
    >
      {!isLiteVersion && (
        <Box
          backgroundColor={
            isOnsiteInterview ? Colors.jobs_interview_background : Colors.jobs_shift_background
          }
          paddingBottom={10}
          paddingTop={26}
        >
          <Text bold color={Colors.primary_500} fontSize={20} textAlign="center">
            {isOnsiteInterview ? 'Interview Details' : 'Shift Details'}
          </Text>
        </Box>
      )}
      <Column {...(hasBorderStyle ? { paddingInline: 30, paddingTop: 20, paddingBottom: 36 } : {})}>
        {!isLiteVersion && (
          <>
            {isCounterHours && (
              <Center data-cy="counter-hours-indicator">
                <PartialShift />
              </Center>
            )}
            {renderOfferExpirationDateTime(currentCounter?.expiration, currentUser?.time_zone)}
          </>
        )}

        <Column gap={9}>
          <Row alignItems="center" justifyContent="space-between">
            <Text bold>Office:</Text>
            <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxWidth={200}>
              {offerOwner?.office_name}
            </Text>
          </Row>
          {isOnsiteInterview && (
            <Row alignItems="center" color={Colors.neutral_600} justifyContent="space-between">
              <Text bold>Job Title:</Text>
              {isLiteVersion ? (
                <Box alignItems="center" justifyContent="flex-end">
                  <Text
                    color={Colors.neutral_500}
                    maxWidth={200}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {job?.postingTitle}
                  </Text>
                </Box>
              ) : (
                <Box alignItems="center" justifyContent="flex-end">
                  <Link to={`/permanent-jobs/job-overview/${job?.postingId}`}>
                    <Text color={Colors.neutral_500} textDecoration="underline">
                      {job?.postingTitle}
                    </Text>
                  </Link>
                </Box>
              )}
            </Row>
          )}
          <Row alignItems="center" justifyContent="space-between">
            <Text bold>Date:</Text>
            <Text>{localDate}</Text>
          </Row>
          <Row alignItems="center" justifyContent="space-between">
            <Text bold>Professional Type:</Text>
            <Text>{fmtProfessionName}</Text>
          </Row>
          <Row alignItems="center" justifyContent="space-between">
            <Text bold>Preferred Specialty:</Text>
            <Text textAlign="right" textTransform="capitalize">
              {EducationUtils.removeSpecialtyPrefix(specialty) || 'General'}
            </Text>
          </Row>
          <HighlightedLines highlight={isCounterHours}>
            <Row data-cy="detail-row-original-hours" justifyContent="space-between">
              <Row gap={8}>
                <Text bold>Hours:</Text>
                <Center
                  backgroundColor={Colors.neutral_300}
                  borderRadius={50}
                  height="min-content"
                  paddingBlock={0}
                  paddingInline={5}
                >
                  <Text color={Colors.white} fontSize={12}>
                    EST
                  </Text>
                </Center>
              </Row>
              <Text
                data-cy="original-hours-value"
                textDecoration={isCounterHours ? 'line-through' : 'none'}
              >
                {`${localStartTime} - ${localEndTime}`}
              </Text>
            </Row>
            {isCounterHours && (
              <Row data-cy="detail-row-hours" justifyContent="space-between">
                <Text bold>*Counter Offer (hours):</Text>
                <Text bold data-cy="hours-value" textTransform="lowercase">
                  {`${currentCounter?.local_begin} - ${currentCounter?.local_end}`}
                </Text>
              </Row>
            )}
          </HighlightedLines>
          <HighlightedLines highlight={isCounterRate}>
            <Row justifyContent="space-between">
              <Text bold>Hourly Rate:</Text>
              <Text
                data-cy="original-rate"
                textDecoration={
                  job?.rate !== currentCounter?.candidateRate ? 'line-through' : 'none'
                }
              >
                ${rate?.toFixed(2) || '0.00'} /hr
              </Text>
            </Row>
            {isCounterRate && (
              <Row justifyContent="space-between">
                <Text bold>*Counter Offer (rate):</Text>
                <Text bold data-cy="counter-rate">
                  ${currentCounter?.candidateRate?.toFixed(2)} /hr
                </Text>
              </Row>
            )}
          </HighlightedLines>
          <Row justifyContent="space-between">
            <Text bold>Meal Break:</Text>
            <Text>
              {job?.lunch_final_break_time} min {job?.is_lunch_break_paid ? ' (Paid)' : ' (Unpaid)'}
            </Text>
          </Row>
          <HighlightedLines highlight={hasOvertimeHours}>
            {hasRegularHours && hasTotalRegular && (
              <Row data-cy="detail-row-regular-pay" justifyContent="space-between">
                <Text bold>
                  Regular Rate Pay (
                  <span data-cy="working-hours-value">
                    {job?.regularAndOvertimeHours?.regular?.toFixed(2)}
                  </span>{' '}
                  hrs):
                </Text>
                <Text data-cy="regular-pay-value">${payment?.hygienistPay?.toFixed(2)}</Text>
              </Row>
            )}
            {hasOvertimeHours && hasTotalOvertime && (
              <Row justifyContent="space-between">
                <Row>
                  <Text bold>
                    *Overtime Pay ({job?.regularAndOvertimeHours?.overtime?.toFixed(2)} hrs):
                  </Text>
                  <span
                    style={{
                      marginLeft: '5px',
                      position: 'relative',
                      bottom: '2px',
                      marginBottom: -10,
                    }}
                  >
                    <SmartTooltip
                      alignment="center"
                      content={OvertimeTooltip}
                      hideArrow
                      position="bottom"
                    >
                      <InfoIcon color={Colors.neutral_300} />
                    </SmartTooltip>
                  </span>
                </Row>
                <Text bold>${job?.totalOvertime?.toFixed(2)}</Text>
              </Row>
            )}
          </HighlightedLines>
          <Row data-cy="detail-row-service-fee" justifyContent="space-between">
            <Text bold>Service Fee{tempMeeFeePercent !== 0 && ` (${tempMeeFeePercent}%)`}:</Text>
            <Text data-cy="service-fee">${tempMeeFee?.toFixed(2)}</Text>
          </Row>
          {payment?.peoFee !== 0 && isEmployeeOptionShift(job) && (
            <Row justifyContent="space-between">
              <Text bold>Employee Fee:</Text>
              <Text>${payment?.peoFee?.toFixed(2)}</Text>
            </Row>
          )}
          {hasConvenienceFee && (
            <Row
              data-cy="detail-row-convenience-fee"
              justifyContent="space-between"
              marginBottom={-8}
            >
              <Row>
                <Text bold>Convenience Fee {`(${convenienceFeePercent || '0'}%)`}:</Text>
                <span
                  style={{
                    marginLeft: '5px',
                    position: 'relative',
                    bottom: '2.5px',
                  }}
                >
                  <SmartTooltip
                    alignment="center"
                    content={ConvenienceFeeTooltip}
                    hideArrow
                    position="bottom"
                  >
                    <InfoIcon color={Colors.neutral_300} />
                  </SmartTooltip>
                </span>
              </Row>
              <Text data-cy="convenience-fee">${convenienceFee?.toFixed(2) || '0'}</Text>
            </Row>
          )}
          {job?.multi_day_booking && startDiff > 28 && (
            <Row justifyContent="space-between">
              <Text bold>Professional Pay Charge Date:</Text>
              <Text>{chargeDate}</Text>
            </Row>
          )}
          {job?.counterCover && (
            <Row data-cy="detail-row-counter-cover" justifyContent="space-between">
              <Text bold data-cy="counter-cover-label">
                Gotu Counter Cover:
              </Text>
              <Text data-cy="counter-cover-value">-$ {job?.counterCover?.amount?.toFixed(2)}</Text>
            </Row>
          )}
          <Row alignItems="center" justifyContent="space-between">
            <Text alignItems="center" bold color={Colors.success_500} columnGap={5}>
              Total Pay:
            </Text>
            <Text bold data-cy="total-pay">
              ${totalAmount?.toFixed(2)}
            </Text>
          </Row>
        </Column>
      </Column>
    </Column>
  );
};
